import { ConvexError } from 'convex/values';

/**
 * Every product module — now literally the billed set: `accounting` is one
 * module (supplier bills, bank import, reconciliation, ledger and VAT) and one
 * billed line. The Business closing and annual accounts keep their rollout
 * kill switch (`STATEMENT_REPORTING_FLAG_ENV`).
 */
export type ModuleKey = 'invoicing' | 'payroll' | 'accounting';

/**
 * The three things a Stripe package prices. Kept as a separate name for the
 * billing code that reads it, but no longer a subset: a product module *is*
 * its billed key, so nothing translates between the two any more.
 */
export type BillingModuleKey = ModuleKey;

/** Canonical order — the order every module set is normalised to. */
export const BILLING_MODULE_KEYS: readonly BillingModuleKey[] = [
  'invoicing',
  'payroll',
  'accounting',
];

export const MODULE_KEYS: readonly ModuleKey[] = BILLING_MODULE_KEYS;

export interface CompanyModules {
  invoicingEnabled?: boolean;
  payrollEnabled?: boolean;
  accountingEnabled?: boolean;
  /**
   * Pre-merge halves of `accountingEnabled`. Read as a fallback until the
   * `accountingEnabled` backfill has run everywhere, then deleted along with
   * the fallback in `isModuleEnabled`.
   */
  expensesEnabled?: boolean;
  financialStatementsEnabled?: boolean;
}

interface ModuleDefinition {
  field: keyof CompanyModules;
  /** Applied when the company row predates the flag. */
  defaultEnabled: boolean;
  disabledError: string;
  /**
   * Deploy-wide kill switch. Convex reads this name, the app reads `VITE_` + it.
   * Optional: a module without one always ships.
   */
  flagEnv?: string;
}

export const MODULES: Record<ModuleKey, ModuleDefinition> = {
  invoicing: {
    field: 'invoicingEnabled',
    defaultEnabled: true,
    disabledError: 'INVOICING_MODULE_DISABLED',
    flagEnv: 'FEATURE_INVOICING',
  },
  payroll: {
    field: 'payrollEnabled',
    defaultEnabled: true,
    disabledError: 'PAYROLL_MODULE_DISABLED',
    flagEnv: 'FEATURE_PAYROLL',
  },
  // Shipped after the module system existed: companies created before it never
  // opted in, so it defaults OFF (unlike the two legacy modules). By design it
  // does NOT inherit from the pre-split `accountingEnabled` field that the
  // `maintenance/migrations/accountingEnabled` migration strips — every
  // company starts fresh (opt back in via settings).
  accounting: {
    field: 'accountingEnabled',
    defaultEnabled: false,
    disabledError: 'ACCOUNTING_MODULE_DISABLED',
    flagEnv: 'FEATURE_ACCOUNTING',
  },
};

/**
 * Parses a module kill-switch env value. Only the literal 'false' disables, so
 * an unset var — the normal case in dev, e2e and CI — leaves the module on.
 */
export function isModuleFlagEnabled(raw: string | undefined): boolean {
  return raw?.trim().toLowerCase() !== 'false';
}

/**
 * Parses an opt-in feature env value — the mirror image of
 * `isModuleFlagEnabled`. Only the literal 'true' enables, so an unset var
 * leaves the feature off everywhere it is not deliberately switched on.
 */
export function isOptInFlagEnabled(raw: string | undefined): boolean {
  return raw?.trim().toLowerCase() === 'true';
}

/**
 * Whether the deployment ships this module at all, independent of any company.
 * The `raw` default reads Convex/Node env; this file is also bundled for the
 * browser (which only calls `isModuleEnabled`), hence the `typeof` guard — the
 * app passes its own `import.meta.env` value via `lib/runtime/feature-flags.ts`.
 */
export function isModuleAvailable(
  module: ModuleKey,
  raw: string | undefined = readEnv(MODULES[module].flagEnv),
): boolean {
  if (MODULES[module].flagEnv === undefined) return true;
  return isModuleFlagEnabled(raw);
}

function readEnv(name: string | undefined): string | undefined {
  if (name === undefined || typeof process === 'undefined') return undefined;
  return process.env[name];
}

export const AVS_CUMULATIVE_FLAG_ENV = 'FEATURE_AVS_CUMULATIVE';
export function isAvsCumulativeAvailable(
  raw = readEnv(AVS_CUMULATIVE_FLAG_ENV),
): boolean {
  return isOptInFlagEnabled(raw);
}

export function socialPayrollSuspended(): boolean {
  return isOptInFlagEnabled(readEnv('FEATURE_SOCIAL_PAYROLL_SUSPENDED'));
}

/**
 * Rolls out the closing and the annual accounts without hiding the rest of
 * the accounting module (documents, bank import, reconciliation, ledger and
 * VAT).
 */
export const STATEMENT_REPORTING_FLAG_ENV = 'FEATURE_FINANCIAL_STATEMENTS';

export function isStatementReportingAvailable(
  raw: string | undefined = readEnv(STATEMENT_REPORTING_FLAG_ENV),
): boolean {
  return isModuleFlagEnabled(raw);
}

export function assertStatementReportingAvailable(): void {
  if (!isStatementReportingAvailable()) {
    throw new ConvexError('FINANCIAL_STATEMENTS_REPORTING_DISABLED');
  }
}

/**
 * The double-entry ledger (journal, chart of accounts, VAT returns) — part of
 * the accounting module for every plan, deployment-gated behind this var.
 * Closing an exercise and the annual accounts are not: they are Business-only
 * and sit behind `STATEMENT_REPORTING_FLAG_ENV` as well
 * (`features/annualAccounts/access.ts`).
 *
 * Opt-in, unlike the module kill switches: only the literal `true` enables it,
 * unset = off. A deployment that knows nothing of the ledger — every existing
 * production image — therefore keeps it closed.
 */
export const LEDGER_FLAG_ENV = 'FEATURE_LEDGER';

export function isLedgerAvailable(
  raw: string | undefined = readEnv(LEDGER_FLAG_ENV),
): boolean {
  return isOptInFlagEnabled(raw);
}

export function assertLedgerAvailable(): void {
  if (!isLedgerAvailable()) {
    throw new Error('LEDGER_DISABLED');
  }
}

/**
 * Migration of issued invoices from another software (`/bills/import`): the
 * AI reading of the old documents and the assistant that turns them into
 * invoices. Opt-in like the ledger: only the literal `true` enables it, unset
 * = off. The Vite half (`VITE_FEATURE_INVOICE_IMPORT`) ships the route.
 */
export const INVOICE_IMPORT_FLAG_ENV = 'FEATURE_INVOICE_IMPORT';

export function isInvoiceImportAvailable(
  raw: string | undefined = readEnv(INVOICE_IMPORT_FLAG_ENV),
): boolean {
  return isOptInFlagEnabled(raw);
}

export function assertInvoiceImportAvailable(): void {
  if (!isInvoiceImportAvailable()) {
    throw new Error('INVOICE_IMPORT_DISABLED');
  }
}

/**
 * Cumulative AC ceiling (payroll/payslips/ceilings/cumulative.ts): the year's earlier
 * months feed each payslip's AC base, a later draft is recomputed when an
 * earlier month changes, and a payslip whose year has unknown months cannot
 * be validated (`PAYROLL_CUMULATIVE_INCOMPLETE`). Off, every month stands
 * alone (the monthly slice, pro-rated on partial months).
 *
 * Opt-in: switched on only once the customers the impact report
 * (maintenance/reports/socialCeilings:reportImpact) flags as blocked have
 * been told. The Vite half (`VITE_FEATURE_CUMULATIVE_CEILINGS`) shows the
 * year-opening form and the incomplete-year banner.
 */
export const CUMULATIVE_CEILINGS_FLAG_ENV = 'FEATURE_CUMULATIVE_CEILINGS';

export function isCumulativeCeilingsAvailable(
  raw: string | undefined = readEnv(CUMULATIVE_CEILINGS_FLAG_ENV),
): boolean {
  return isOptInFlagEnabled(raw);
}

/**
 * Emergency stop of the automatic invoice reminders, with no redeployment:
 * `bunx convex env set FEATURE_INVOICE_REMINDERS false`. Unset leaves them on,
 * which sends nothing by itself: a company has to switch them on first. Read
 * at the start of each action, so a run under way may finish its slice.
 */
export const INVOICE_REMINDERS_FLAG_ENV = 'FEATURE_INVOICE_REMINDERS';

export function isInvoiceRemindersAvailable(
  raw: string | undefined = readEnv(INVOICE_REMINDERS_FLAG_ENV),
): boolean {
  return isModuleFlagEnabled(raw);
}

export function isModuleEnabled(
  company: CompanyModules | null | undefined,
  module: ModuleKey,
) {
  if (!company) return false;
  const { field, defaultEnabled } = MODULES[module];
  if (module === 'accounting') {
    // Rollout fallback: a row the backfill has not reached yet still carries
    // the two pre-merge halves, and the merge is their OR (same rule as
    // `planAccountingModuleBackfill`). Dropped with the two fields once the
    // backfill has run everywhere.
    // Both halves default OFF, like the merged module, so an untouched row
    // still lands on `defaultEnabled`.
    return (
      company.accountingEnabled ??
      ((company.expensesEnabled ?? defaultEnabled) ||
        (company.financialStatementsEnabled ?? defaultEnabled))
    );
  }
  return company[field] ?? defaultEnabled;
}

export function assertModuleEnabled(
  company: CompanyModules | null | undefined,
  module: ModuleKey,
) {
  // The deploy-wide switch wins: a company flag cannot re-open a dark module.
  if (!isModuleAvailable(module) || !isModuleEnabled(company, module)) {
    throw new Error(MODULES[module].disabledError);
  }
}

/** Independent opt-in for dated LAA coverage and cumulative allocation. */
export const LAA_MUTATIONS_FLAG_ENV = 'FEATURE_LAA_MUTATIONS';
export function isLaaMutationsAvailable(
  raw: string | undefined = readEnv(LAA_MUTATIONS_FLAG_ENV),
): boolean {
  return isOptInFlagEnabled(raw);
}
